import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { useProductVariants } from '../../hooks';
import { formatAttributeValueDisplay } from '../../lib/attributeValueDisplay';
import { formatPrice } from '../../lib/format';
import { PRODUCT_CARD_STATUS_LABELS, productCardStatusColor } from '../../model/options';
import type { AttributeDefinition, ProductFamily } from '../../model/types';
import type { AxisAttribute } from './CreateVariantsDialog';
import { CreateVariantsDialog } from './CreateVariantsDialog';
import { GenerateVariantMatrixDialog } from './GenerateVariantMatrixDialog';

export interface ProductVariantsSectionProps {
  productId: string;
  productCode?: string;
  assignedFamily: ProductFamily;
  dictionary: AttributeDefinition[];
  canManage: boolean;
  onNotify: (severity: 'success' | 'error' | 'info' | 'warning', message: string) => void;
}

/**
 * Variants list + "Create variants" entry point (user-story-merchant-portal-031). Only rendered
 * by `ProductEditPage` when the product has an assigned family with ≥1 `variantAxis` attribute
 * and is not itself a variant (`parentProductId == null`) — see that page's render condition.
 */
export function ProductVariantsSection({
  productId,
  productCode,
  assignedFamily,
  dictionary,
  canManage,
  onNotify,
}: ProductVariantsSectionProps) {
  const { t } = useTranslation('catalog');
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(false);

  const variantsQuery = useProductVariants(productId);
  const variants = variantsQuery.data ?? [];

  const axisCodes = useMemo(
    () => assignedFamily.attributes.filter((a) => a.variantAxis).map((a) => a.attributeCode),
    [assignedFamily],
  );

  const axisAttributes: AxisAttribute[] = useMemo(
    () =>
      axisCodes
        .map((code) => {
          const definition = dictionary.find((d) => d.code === code);
          return definition ? { attributeCode: code, definition } : null;
        })
        .filter((a): a is AxisAttribute => a !== null),
    [axisCodes, dictionary],
  );

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {t('productVariantsSection.subtitle')}
        </Typography>
        {canManage ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="text"
              onClick={() => setMatrixOpen(true)}
              disabled={axisAttributes.length === 0}
            >
              {t('productVariantsSection.generateMatrixButton')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddRoundedIcon />}
              onClick={() => setCreateOpen(true)}
              disabled={axisAttributes.length === 0}
            >
              {t('productVariantsSection.createButton')}
            </Button>
          </Stack>
        ) : null}
      </Stack>

      {variantsQuery.isLoading ? (
        <LoadingState label={t('productVariantsSection.loading')} />
      ) : variantsQuery.isError ? (
        <ErrorState
          title={t('productVariantsSection.loadError.title')}
          message={variantsQuery.error?.message ?? t('common:error.unknown')}
          onRetry={() => void variantsQuery.refetch()}
        />
      ) : variants.length === 0 ? (
        <EmptyState
          title={t('productVariantsSection.empty.title')}
          description={t('productVariantsSection.empty.description')}
          icon={<CallSplitRoundedIcon />}
        />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('productVariantsSection.table.axisValues')}</TableCell>
                <TableCell>{t('productVariantsSection.table.name')}</TableCell>
                <TableCell>{t('productVariantsSection.table.code')}</TableCell>
                <TableCell align="right">{t('productVariantsSection.table.price')}</TableCell>
                <TableCell>{t('productVariantsSection.table.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variants.map((variant) => (
                <TableRow
                  key={variant.id}
                  hover
                  onClick={() => navigate(`/catalog/products/${variant.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {axisCodes.map((code) => {
                        const value = variant.attributeValues?.find((v) => v.code === code);
                        if (!value) return null;
                        return (
                          <Chip key={code} size="small" variant="outlined" label={formatAttributeValueDisplay(value)} />
                        );
                      })}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200 }}>
                      {variant.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box component="code" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>
                      {variant.code ?? '—'}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{formatPrice(variant.price)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={productCardStatusColor(variant.cardStatus ?? 'ACTIVE')}
                      label={t(
                        PRODUCT_CARD_STATUS_LABELS[variant.cardStatus ?? 'ACTIVE'] ?? PRODUCT_CARD_STATUS_LABELS.ACTIVE,
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {axisAttributes.length === 0 && canManage ? (
        <Typography variant="caption" color="text.secondary">
          {t('productVariantsSection.axisDefinitionsMissingHint', { codes: axisCodes.join(', ') })}
        </Typography>
      ) : null}

      <CreateVariantsDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        productId={productId}
        parentCode={productCode ?? ''}
        axisAttributes={axisAttributes}
        onResult={onNotify}
      />
      <GenerateVariantMatrixDialog
        open={matrixOpen}
        onClose={() => setMatrixOpen(false)}
        productId={productId}
        axisAttributes={axisAttributes}
        onGenerated={(result) =>
          onNotify(
            'success',
            t('generateMatrixDialog.doneMessage', {
              created: result.created?.length ?? 0,
              skipped: result.skipped?.length ?? 0,
            }),
          )
        }
      />
    </Stack>
  );
}
